import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { UpcomingItem } from './UpcomingItem';

export const UpcomingProjectCard = ({ p, stallDays, onToggleTask, onToggleTodo, onDeleteTodo, onLoadChecklist }) => {
  const navigate = useNavigate();
  const lastDays = p.last_activity ? Math.floor((Date.now() - new Date(p.last_activity).getTime()) / 86400000) : null;
  const empty = !p.next_up.length && !p.todos.length;

  return (
    <section data-testid={`upcoming-project-${p.project_id}`}
      className={`bg-white rounded-lg border overflow-hidden ${p.stalled ? 'border-red-300 ring-1 ring-red-100' : 'border-stone-200'}`}>
      <header className="px-4 py-3 border-b border-stone-100 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]">
          <p className="text-[10px] uppercase tracking-wider text-stone-500">{p.project_type}</p>
          <h2 className="text-base font-semibold text-stone-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{p.project_name}</h2>
        </div>
        {p.total > 0 && (
          <div className="flex items-center gap-2 w-40">
            <Progress value={(p.done / p.total) * 100} className="h-1.5 flex-1" />
            <span className="text-[11px] text-stone-500 tabular-nums" data-testid={`upcoming-progress-${p.project_id}`}>{p.done}/{p.total}</span>
          </div>
        )}
        <button onClick={() => navigate(`/admin/projects/${p.project_id}/tasks`)} data-testid={`upcoming-open-${p.project_id}`}
          className="text-xs text-stone-600 hover:text-stone-900 inline-flex items-center gap-1">
          Full checklist <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      </header>
      {p.stalled && (
        <div className="px-4 py-2 bg-red-50 text-red-800 text-xs flex items-center gap-2" data-testid={`upcoming-stalled-${p.project_id}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Stalled — nothing completed in {lastDays ?? stallDays}+ days
        </div>
      )}
      <div className="divide-y divide-stone-100">
        {p.todos.map(t => <UpcomingItem key={t.todo_id} item={t} kind="todo" onToggle={onToggleTodo} onDelete={onDeleteTodo} />)}
        {p.next_up.map(t => <UpcomingItem key={t.task_id} item={t} kind="task" onToggle={onToggleTask} />)}
      </div>
      {empty && p.total === 0 && (
        <div className="px-4 py-4 flex items-center justify-between gap-3 text-sm text-stone-600">
          No checklist yet.
          <Button size="sm" onClick={() => onLoadChecklist(p)} data-testid={`upcoming-load-${p.project_id}`} className="bg-stone-900 hover:bg-stone-800 text-white">Load checklist</Button>
        </div>
      )}
      {empty && p.total > 0 && (
        <div className="px-4 py-4 text-sm text-lime-800 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> All tasks done</div>
      )}
    </section>
  );
};
